import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { updateCompanySettings, fetchCompanySettings } from '../../../slice/HomeSlice';
import {
  EmailSMSConfigContent,
  EmailSMSConfigContentHeader,
  EmailSMSConfigContentBody,
  EmailSMSConfigForm,
  EmailSMSConfigLabel,
  EmailSMSConfigInput,
  EmailSMSConfigUploadBox,
  EmailSMSConfigUpdateButton,
  ErrorMessageWrapper,
  ErrorText,
  PreviewBox,
  PreviewHeader,
  PreviewTitle,
  RemoveButton,
  PreviewImageWrapper,
  PreviewImage,
} from './EmailSmsStyled';
import DeleteRed from "../../../assets/images/DeleteRed.svg";
import { ConfirmModal } from "../../../components/common/Modals";

const GeneralConfig = ({ setIsLoading, handleToaster }) => {
  const dispatch = useDispatch();
  const companySettings = useSelector((state) => state.home.companySettings);

  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [logo, setLogo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [logoDetails, setLogoDetails] = useState({
    logo_url: null,
    logo_filename: null,
  });

  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [logoError, setLogoError] = useState('');
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchCompanySettings())
      .unwrap()
      .then(() => setIsLoading(false))
      .catch((error) => handleToaster(error?.message || 'Failed to fetch company settings'));
  }, [dispatch]);

  useEffect(() => {
    const data = companySettings?.data;
    if (data) {
      setCompanyEmail(data.company_email || '');
      setCompanyPhone(data.company_phone || '');
      setLogoDetails({
        logo_url: data.logo_url || null,
        logo_filename: data.logo_filename || null,
      });
    }
  }, [companySettings?.data]);

  const handleLogoChange = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      handleToaster('Please upload a valid image file.', false);
      return;
    }

    setLogo(file);
    setPreviewUrl(URL.createObjectURL(file));
    setLogoError('');
    setUpdateButtonDisabled(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10,15}$/;
    if (!phone) return 'Phone number is required';
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number (10-15 digits)';
    return '';
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setCompanyEmail(email);
    setEmailError(validateEmail(email));
    setUpdateButtonDisabled(false);
  };

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setCompanyPhone(phone);
    setPhoneError(validatePhone(phone));
    setUpdateButtonDisabled(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault?.();

    const emailValidationError = validateEmail(companyEmail);
    const phoneValidationError = validatePhone(companyPhone);
    const isLogoMissing = !logo && !logoDetails.logo_url;

    setEmailError(emailValidationError);
    setPhoneError(phoneValidationError);
    setLogoError(isLogoMissing ? 'Company logo is required' : '');

    if (emailValidationError || phoneValidationError || isLogoMissing) return;

    setIsLoading(true);

    const formData = new FormData();
    formData.append('company_email', companyEmail);
    formData.append('company_phone', companyPhone);
    if (logo) formData.append('logo', logo);

    try {
      await dispatch(updateCompanySettings(formData)).unwrap();
      handleToaster('Company settings updated successfully', true);
      setEmailError('');
      setPhoneError('');
      setLogoError('');
    } catch (error) {
      console.error('Error updating company settings:', error);
      handleToaster(error?.message || 'Something went wrong. Please try again.', false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoRemove = () => {
    if(logo && previewUrl) {
      setLogo(null);
    } else if(!logo && logoDetails.logo_url) {
      setLogoDetails({ logo_url: null, logo_filename: null });
    }
    setPreviewUrl(null);
    setUpdateButtonDisabled(false);
    setConfirmModalOpen(false);
  };

  const handleConfirmModal = (e) => {
    e.preventDefault?.();
    setConfirmModalOpen(true);
  };

  return (
    <EmailSMSConfigContent>
      <EmailSMSConfigContentHeader>
        General Configurations
        <EmailSMSConfigUpdateButton disabled={emailError || phoneError || updateButtonDisabled} onClick={handleSubmit}>
          Update
        </EmailSMSConfigUpdateButton>
      </EmailSMSConfigContentHeader>

      <EmailSMSConfigContentBody>
        <EmailSMSConfigForm onSubmit={handleSubmit}>
          {/* Email */}
          <div>
            <EmailSMSConfigLabel htmlFor="company-email">Company Email Address</EmailSMSConfigLabel>
            <br />
            <EmailSMSConfigInput
              id="company-email"
              type="email"
              value={companyEmail}
              onChange={handleEmailChange}
              placeholder="Enter company email"
              style={emailError ? { background: '#FBEFEF', border: '1px solid #D97D7D' } : {}}
            />
            {emailError && (
              <ErrorMessageWrapper>
                <img src={require('../../../assets/images/Error.png')} alt="Error" style={{ width: 12, height: 12 }} />
                <ErrorText>{emailError}</ErrorText>
              </ErrorMessageWrapper>
            )}
          </div>

          {/* Phone */}
          <div>
            <EmailSMSConfigLabel htmlFor="company-phone">Company Phone Number</EmailSMSConfigLabel>
            <br />
            <EmailSMSConfigInput
              id="company-phone"
              type="text"
              value={companyPhone}
              onChange={handlePhoneChange}
              placeholder="Enter company phone number"
              style={phoneError ? { background: '#FBEFEF', border: '1px solid #D97D7D' } : {}}
            />
            {phoneError && (
              <ErrorMessageWrapper>
                <img src={require('../../../assets/images/Error.png')} alt="Error" style={{ width: 12, height: 12 }} />
                <ErrorText>{phoneError}</ErrorText>
              </ErrorMessageWrapper>
            )}
          </div>

          {/* Logo */}
          <div>
            <EmailSMSConfigLabel>Company Logo</EmailSMSConfigLabel>

            {/* Preview for uploaded logo */}
            {logo && previewUrl && (
              <PreviewBox>
                <PreviewHeader>
                  <PreviewTitle>
                    <img src={require('../../../assets/images/Pin.png')} alt="Pin" style={{ width: 24, height: 24 }} />
                    <span>{logo.name}</span>
                  </PreviewTitle>
                  <RemoveButton onClick={handleConfirmModal}>
                    <img src={DeleteRed} alt="Remove" style={{ width: 24, height: 24 }} />
                  </RemoveButton>
                </PreviewHeader>
                <PreviewImageWrapper>
                  <PreviewImage src={previewUrl} alt="Company Logo Preview" />
                </PreviewImageWrapper>
              </PreviewBox>
            )}

            {/* Existing logo */}
            {!logo && logoDetails.logo_url && (
              <PreviewBox>
                <PreviewHeader>
                  <PreviewTitle>
                    <img src={require('../../../assets/images/Pin.png')} alt="Pin" style={{ width: 24, height: 24 }} />
                    <span>{logoDetails.logo_filename}</span>
                  </PreviewTitle>
                  <RemoveButton onClick={handleConfirmModal}>
                    <img src={DeleteRed} alt="Remove" style={{ width: 24, height: 24 }} />
                  </RemoveButton>
                </PreviewHeader>
                <PreviewImageWrapper>
                  <PreviewImage src={logoDetails.logo_url} alt="Company Logo" />
                </PreviewImageWrapper>
              </PreviewBox>
            )}

            {/* Upload Box */}
            {!logo && !logoDetails.logo_url && (
              <>
                <label htmlFor="company-logo-upload">
                  <EmailSMSConfigUploadBox>
                    <img src={require('../../../assets/images/Pin.png')} alt="Pin" style={{ width: 24, height: 24 }} />
                    <span style={{ marginLeft: 8 }}>Upload Logo</span>
                    <img src={require('../../../assets/images/UploadFrame.png')} alt="Upload" style={{ width: 18, height: 18, marginLeft: 'auto' }} />
                    <input
                      id="company-logo-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleLogoChange}
                    />
                  </EmailSMSConfigUploadBox>
                </label>
                {logoError && (
                  <ErrorMessageWrapper>
                    <img src={require('../../../assets/images/Error.png')} alt="Error" style={{ width: 12, height: 12 }} />
                    <ErrorText>{logoError}</ErrorText>
                  </ErrorMessageWrapper>
                )}
              </>
            )}
          </div>
          <ConfirmModal
            isOpen={confirmModalOpen}
            title="Delete Company Logo?"
            message="This will remove the company logo. Are you sure you want to proceed?"
            cancelText="Cancel"
            confirmText="Delete"
            onCancel={() => setConfirmModalOpen(false)}
            onConfirm={handleLogoRemove}
          />
        </EmailSMSConfigForm>
      </EmailSMSConfigContentBody>
    </EmailSMSConfigContent>
  );
};

GeneralConfig.propTypes = {
  setIsLoading: PropTypes.func.isRequired,
  handleToaster: PropTypes.func.isRequired,
};

export default GeneralConfig;
